import {
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';

/**
 * Interface representing the data associated with each petal
 * @interface PetalData
 * @property {number} speed - The vertical falling speed of the petal
 * @property {number} wobbleSpeed - The speed of the horizontal wobble movement
 * @property {number} wobbleRange - The maximum horizontal distance the petal can wobble
 * @property {number} rotationSpeed - The speed of rotation of the petal
 */
interface PetalData {
  speed: number;
  wobbleSpeed: number;
  wobbleRange: number;
  rotationSpeed: number;
}

/**
 * A class that creates and manages an animated sakura petal falling effect
 * @extends View
 *
 * @example
 * const sakuraEffect = new SakuraEffect({
 *   superview: parentView,
 *   width: 800,
 *   height: 600
 * });
 *
 * // Start the sakura effect
 * sakuraEffect.start();
 *
 * // Later, stop the effect
 * sakuraEffect.stop();
 */
export class SakuraEffect extends View {
  /** Number of petals to create */
  private static readonly NUM_PETALS = 50;
  /** Minimum scale factor for petals */
  private static readonly MIN_SCALE = 0.5;
  /** Range of random scale variation */
  private static readonly SCALE_RANGE = 0.7;
  /** Base speed for petal falling motion */
  private static readonly BASE_SPEED = 0.02;
  /** Base speed for petal wobble motion */
  private static readonly WOBBLE_BASE = 0.001;
  /** Maximum range of horizontal wobble motion */
  private static readonly WOBBLE_RANGE = 40;
  /** Base rotation speed for petals */
  private static readonly ROTATION_BASE = 0.0001;
  /** Chance of slowing down vertical speed */
  private static readonly FLOAT_CHANCE = 0.03;

  /** Array of petal view instances */
  private readonly petals: ImageScaleView[] = [];
  /** Array of data objects containing movement parameters for each petal */
  private readonly petalData: PetalData[] = [];

  /** ID of the current animation frame request */
  private animationFrameId: number | null = null;
  /** Timestamp of the last animation update */
  private lastTimestamp = 0;
  /** Flag indicating whether the sakura effect is currently active */
  private isActive = false;

  /**
   * Creates a new SakuraEffect instance
   * @param {ViewOpts} opts - Configuration options for the view
   *
   * @example
   * const sakuraEffect = new SakuraEffect({
   *   superview: parentView,
   *   width: 800,
   *   height: 600
   * });
   */
  constructor(opts: ViewOpts) {
    super({ ...opts, visible: false });
    this.initializePetals();
  }

  /**
   * Initializes the petals for the sakura effect
   *
   * Creates a set of petal instances based on NUM_PETALS constant.
   * Each petal is given random properties within defined ranges for:
   * - Scale (affects size)
   * - Position (within view bounds)
   * - Movement parameters (speed, wobble)
   *
   * @private
   */
  private initializePetals(): void {
    const { width, height } = this.style;

    for (let i = 0; i < SakuraEffect.NUM_PETALS; i++) {
      const scale =
        Math.random() * SakuraEffect.SCALE_RANGE + SakuraEffect.MIN_SCALE;

      this.petals.push(
        new ImageScaleView({
          superview: this,
          image: 'assets/ui/match2/common/particle_sakura.png',
          width: 12,
          height: 12,
          scale,
          x: Math.random() * width,
          y: Math.random() * height,
          centerOnOrigin: true,
        }),
      );

      this.petalData.push({
        speed: scale * SakuraEffect.BASE_SPEED,
        wobbleSpeed: SakuraEffect.WOBBLE_BASE * scale,
        wobbleRange: SakuraEffect.WOBBLE_RANGE,
        rotationSpeed: SakuraEffect.ROTATION_BASE * (Math.random() * 2 - 1), // Random direction
      });
    }
  }

  /**
   * Starts the sakura petal falling effect animation
   *
   * Makes the effect visible with a fade-in animation and initiates
   * the animation loop using requestAnimationFrame.
   * Does nothing if the effect is already active.
   *
   * @public
   *
   * @example
   * sakuraEffect.start();
   */
  public start(): void {
    if (this.isActive) return;

    this.isActive = true;
    this.updateOpts({ visible: true, opacity: 0 });
    animate(this).now({ opacity: 1 }, 500);

    this.lastTimestamp = performance.now();
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
  }

  /**
   * Stops the sakura petal falling effect animation
   *
   * Fades out the effect and stops the animation loop.
   * Sets the view to invisible after fade-out completes.
   * Does nothing if the effect is already inactive.
   *
   * @public
   *
   * @example
   * sakuraEffect.stop();
   */
  public stop(): void {
    if (!this.isActive) return;

    this.isActive = false;
    animate(this)
      .now({ opacity: 0 }, 500)
      .then(() => this.updateOpts({ visible: false }));

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Handles the animation frame updates
   *
   * Called by requestAnimationFrame to update petal positions
   * and schedule the next frame if the effect is still active.
   *
   * @param {number} timestamp - The current timestamp from requestAnimationFrame
   * @private
   */
  private animate(timestamp: number): void {
    if (!this.isActive) return;

    this.updatePetals(timestamp);
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
  }

  /**
   * Updates the position of all petals
   *
   * Calculates new positions based on:
   * - Time elapsed since last update (for consistent speed)
   * - Individual petal parameters (speed, wobble)
   * - View boundaries (for wrapping)
   *
   * @param {number} timestamp - The current animation timestamp
   * @private
   */
  private updatePetals(timestamp: number): void {
    const dt = this.lastTimestamp ? timestamp - this.lastTimestamp : 0;
    this.lastTimestamp = timestamp;

    const { width, height } = this.style;

    for (let i = 0; i < this.petals.length; i++) {
      const petal = this.petals[i];
      const data = this.petalData[i];

      // Check for entering floating
      if (Math.random() < SakuraEffect.FLOAT_CHANCE) {
        // Slow down gradually
        data.speed = Math.max(data.speed * 0.3, SakuraEffect.BASE_SPEED * 0.2);
      } else if (data.speed < SakuraEffect.BASE_SPEED) {
        // Gradually return to normal speed
        data.speed = Math.min(SakuraEffect.BASE_SPEED, data.speed * 1.1);
      }

      // Update horizontal position (wobble)
      petal.style.offsetX =
        Math.sin(timestamp * data.wobbleSpeed) * data.wobbleRange;

      // Update rotation
      petal.style.r = (petal.style.r || 0) + dt * data.rotationSpeed * 30;

      // Update vertical position
      petal.style.y += dt * data.speed;

      // Reset position if petal is below screen
      if (petal.style.y > height + petal.style.height) {
        petal.style.x = Math.random() * width;
        petal.style.y = -petal.style.height;
      }
    }
  }
}
